import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import styled from 'styled-components';
import useDocumentTitle from '../../components/auth/useDocumentTitle';
import { btnPrimary, btnSecondary } from '../../styles/buttonStyles';
import api from '../../utils/api';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface Publication {
  id: number;
  name: string;
}

interface TagData {
  id?: number;
  name: string;
  position: number;
  brand_profile_ids: number[];
}

// Row state carries a stable client-side key so react-beautiful-dnd has a
// constant `draggableId` across reorders. The array index is NOT stable under
// drag-and-drop (it changes as rows move), and a new tag has no persisted `id`,
// so neither can serve as the draggableId.
interface TagRowState extends TagData {
  rowKey: number;
}

let tagRowKeySeq = 0;
const nextTagRowKey = (): number => {
  tagRowKeySeq += 1;
  return tagRowKeySeq;
};

interface TagGroupData {
  id: number | null;
  name: string;
  tags: TagData[];
}

interface FormResponse {
  tag_group: TagGroupData;
  publications: Publication[];
}

/* ------------------------------------------------------------------ */
/*  Styled components                                                  */
/* ------------------------------------------------------------------ */

const PageContainer = styled.div`
  padding: 24px;
  max-width: 900px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  margin-bottom: 32px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin: 0;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
`;

const ErrorState = styled.div`
  padding: 20px;
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #dc2626;
`;

const FormError = styled.div`
  padding: 12px 16px;
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  color: #dc2626;
  font-size: 14px;
  margin-bottom: 20px;
`;

const FormGroup = styled.div`
  margin-bottom: 24px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 6px;
`;

const RequiredMark = styled.span`
  color: #dc2626;
  margin-left: 2px;
`;

const TextInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  font-size: 14px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  color: #333;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #00a6c2;
    box-shadow: 0 0 0 2px rgba(0, 166, 194, 0.15);
  }
`;

const TagNameInput = styled(TextInput)`
  width: 200px;
`;

const SectionLabel = styled.h2`
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0 0 12px 0;
`;

const TagsTable = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  background: #fff;
  overflow-x: auto;
`;

const TagsTableHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 12px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  gap: 12px;
  min-width: fit-content;
`;

const HeaderCell = styled.div<{ width?: string }>`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #666;
  width: ${(props) => props.width || 'auto'};
  flex-shrink: 0;
  text-align: center;

  &:first-child {
    text-align: left;
  }
`;

const TagRow = styled.div<{ $isDragging?: boolean }>`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-bottom: 1px solid #e5e7eb;
  gap: 12px;
  min-width: fit-content;
  background: ${(props) => (props.$isDragging ? '#f9fafb' : '#fff')};

  &:last-child {
    border-bottom: none;
  }
`;

// Vertical drag handle restoring the legacy `.sort-handle.tag-sort-handle`
// affordance (axis: "y") that the migration dropped — eng-maintenance#21552.
const DragHandle = styled.span`
  width: 24px;
  flex-shrink: 0;
  cursor: grab;
  color: #9ca3af;
  font-size: 16px;
  line-height: 1;
  user-select: none;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #4b5563;
  }
  &:active {
    cursor: grabbing;
  }
`;

const TagNameCell = styled.div`
  width: 200px;
  flex-shrink: 0;
`;

const PubCheckboxCell = styled.div`
  width: 80px;
  flex-shrink: 0;
  text-align: center;

  input {
    width: 16px;
    height: 16px;
    cursor: pointer;
  }
`;

const RemoveTagButton = styled.button`
  background: none;
  border: none;
  color: #dc2626;
  font-size: 18px;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 4px;
  line-height: 1;
  flex-shrink: 0;

  &:hover {
    background: #fef2f2;
  }
`;

const AddTagButton = styled.button`
  ${btnSecondary}
  margin-top: 12px;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 32px;
`;

const PrimaryButton = styled.button`
  ${btnPrimary}
`;

const CancelButton = styled.a`
  ${btnSecondary}
`;

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function OrgTagGroupFormPage() {
  const { orgId, tagGroupId } = useParams<{ orgId: string; tagGroupId?: string }>();

  const isEdit = Boolean(tagGroupId);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState('');
  const [tags, setTags] = useState<TagRowState[]>([]);
  // parity-audit-r2: track removed persisted tags so the BE actually destroys them
  const [removedTagIds, setRemovedTagIds] = useState<number[]>([]);
  const [publications, setPublications] = useState<Publication[]>([]);
  const [originalName, setOriginalName] = useState('');

  const pageTitle = isEdit ? `Edit ${originalName || 'Tag Group'}` : 'Create Tags';
  useDocumentTitle(`${pageTitle} : Contently`);

  const fetchFormData = useCallback(async () => {
    if (!orgId) return;
    try {
      setLoading(true);
      setError(null);

      const url = isEdit
        ? `/organizations/${orgId}/tag_groups/${tagGroupId}/edit.json`
        : `/organizations/${orgId}/tag_groups/new.json`;

      const res = await fetch(url, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });

      if (!res.ok) throw new Error('Failed to load form data');

      const data: FormResponse = await res.json();
      setName(data.tag_group.name || '');
      setOriginalName(data.tag_group.name || '');
      const loadedTags: TagData[] =
        data.tag_group.tags.length > 0
          ? data.tag_group.tags
          : [{ name: '', position: 0, brand_profile_ids: [] }];
      setTags(loadedTags.map((t) => ({ ...t, rowKey: nextTagRowKey() })));
      setPublications(data.publications || []);
      setRemovedTagIds([]); // parity-audit-r2: clear pending destroys on (re)load
    } catch (err: any) {
      setError(err.message || 'Failed to load form data');
    } finally {
      setLoading(false);
    }
  }, [orgId, tagGroupId, isEdit]);

  useEffect(() => {
    fetchFormData();
  }, [fetchFormData]);

  const handleTagNameChange = (index: number, value: string) => {
    setTags((prev) => prev.map((t, i) => (i === index ? { ...t, name: value } : t)));
  };

  const handleTagPubToggle = (tagIndex: number, pubId: number) => {
    setTags((prev) =>
      prev.map((t, i) => {
        if (i !== tagIndex) return t;
        const ids = t.brand_profile_ids.includes(pubId)
          ? t.brand_profile_ids.filter((id) => id !== pubId)
          : [...t.brand_profile_ids, pubId];
        return { ...t, brand_profile_ids: ids };
      }),
    );
  };

  const handleAddTag = () => {
    setTags((prev) => [
      ...prev,
      { name: '', position: prev.length, brand_profile_ids: [], rowKey: nextTagRowKey() },
    ]);
  };

  // Reorder the in-memory tags array on drop. handleSubmit serializes
  // `position: idx` from this order, so reordering here is what persists the
  // new tag order — restoring the legacy SortableView behavior (#21552).
  const handleDragEnd = (result: DropResult) => {
    if (!result.destination || result.source.index === result.destination.index) return;
    // Capture indices as primitives before the setTags closure — TS control-flow
    // narrowing of `result.destination` does not carry into a nested callback.
    const from = result.source.index;
    const to = result.destination.index;
    setTags((prev) => {
      const reordered = Array.from(prev);
      const [moved] = reordered.splice(from, 1);
      reordered.splice(to, 0, moved);
      return reordered;
    });
  };

  const handleRemoveTag = (index: number) => {
    setTags((prev) => {
      const removed = prev[index];
      // parity-audit-r2: remember persisted tags so handleSubmit can _destroy them
      if (removed?.id) {
        setRemovedTagIds((ids) => (ids.includes(removed.id!) ? ids : [...ids, removed.id!]));
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orgId || !name.trim()) return;

    setSaving(true);
    setFormError(null);

    // Build tags_attributes in Rails nested attributes format
    const tagsAttributes: Record<string, any> = {};
    tags.forEach((tag, idx) => {
      const attr: any = {
        name: tag.name,
        position: idx,
        brand_profile_ids: tag.brand_profile_ids,
      };
      if (tag.id) {
        attr.id = tag.id;
      }
      tagsAttributes[String(idx)] = attr;
    });
    // parity-audit-r2: append _destroy entries for removed persisted tags using next free numeric keys
    removedTagIds.forEach((id, i) => {
      tagsAttributes[String(tags.length + i)] = { id, _destroy: true };
    });

    const payload = {
      taxonomy_tag_group: {
        name: name.trim(),
        tags_attributes: tagsAttributes,
      },
    };

    const url = isEdit
      ? `/organizations/${orgId}/tag_groups/${tagGroupId}.json`
      : `/organizations/${orgId}/tag_groups.json`;

    try {
      if (isEdit) {
        await api.patch(url, payload);
      } else {
        await api.post(url, payload);
      }
      window.location.href = `/organizations/${orgId}/tag_groups`;
    } catch (err: any) {
      const messages = err.response?.data?.errors?.join(', ') || 'Failed to save tag group';
      setFormError(messages);
    } finally {
      setSaving(false);
    }
  };

  if (!orgId) {
    return (
      <PageContainer>
        <ErrorState>Organization ID is required</ErrorState>
      </PageContainer>
    );
  }

  if (loading) {
    return (
      <PageContainer>
        <LoadingState>Loading...</LoadingState>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <ErrorState>{error}</ErrorState>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <Title>{pageTitle}</Title>
      </PageHeader>

      <form onSubmit={handleSubmit}>
        {formError && <FormError>{formError}</FormError>}

        <FormGroup>
          <Label htmlFor="tag-group-name">
            Tag Group Name<RequiredMark>*</RequiredMark>
          </Label>
          <TextInput
            id="tag-group-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
          />
        </FormGroup>

        <FormGroup>
          <SectionLabel>Tags</SectionLabel>
          <TagsTable>
            <TagsTableHeader>
              <HeaderCell width="24px" />
              <HeaderCell width="200px">Tag Name</HeaderCell>
              {publications.map((pub) => (
                <HeaderCell key={pub.id} width="80px">
                  {pub.name}
                </HeaderCell>
              ))}
              <HeaderCell width="40px" />
            </TagsTableHeader>
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="org-tags">
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    {tags.map((tag, idx) => (
                      <Draggable key={tag.rowKey} draggableId={String(tag.rowKey)} index={idx}>
                        {(dragProvided, snapshot) => (
                          <TagRow
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            $isDragging={snapshot.isDragging}
                          >
                            <DragHandle
                              {...dragProvided.dragHandleProps}
                              title="Drag to reorder"
                              aria-label="Drag to reorder"
                            >
                              &#x283F;
                            </DragHandle>
                            <TagNameCell>
                              <TagNameInput
                                type="text"
                                value={tag.name}
                                onChange={(e) => handleTagNameChange(idx, e.target.value)}
                                placeholder="Tag name"
                              />
                            </TagNameCell>
                            {publications.map((pub) => (
                              <PubCheckboxCell key={pub.id}>
                                <input
                                  type="checkbox"
                                  checked={tag.brand_profile_ids.includes(pub.id)}
                                  onChange={() => handleTagPubToggle(idx, pub.id)}
                                />
                              </PubCheckboxCell>
                            ))}
                            <RemoveTagButton
                              type="button"
                              onClick={() => handleRemoveTag(idx)}
                              title="Remove tag"
                              aria-label="Remove tag"
                            >
                              &times;
                            </RemoveTagButton>
                          </TagRow>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </TagsTable>
          <AddTagButton type="button" onClick={handleAddTag}>
            + Add Tag
          </AddTagButton>
        </FormGroup>

        <ButtonRow>
          <PrimaryButton type="submit" disabled={saving || !name.trim()}>
            {saving ? 'Saving...' : 'Save'}
          </PrimaryButton>
          <CancelButton href={`/organizations/${orgId}/tag_groups`}>Cancel</CancelButton>
        </ButtonRow>
      </form>
    </PageContainer>
  );
}
